import { useState, useEffect } from 'react';
import api from './api';

const FARMS = ['1场', '2场', '3场', '4场', '5场'];

function getActiveFormula(formulas, product, date) {
  return formulas
    .filter(f => f.product === product && f.effective_date <= date)
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date))[0];
}

function planSummary(planItems) {
  if (!planItems?.length) return '—';
  return planItems.map(p => `${p.farm}${p.planned_trucks}车`).join(' / ');
}

function calcStatus(planItems, arrivals) {
  const planned = (planItems || []).reduce((s, p) => s + p.planned_trucks * p.tons_per_truck, 0);
  const actual = (arrivals || []).reduce((s, a) => s + parseFloat(a.tons), 0);
  const diff = actual - planned;
  if (actual === 0) return { label: '待到货', color: '#94a3b8' };
  if (Math.abs(diff) < 0.1) return { label: '✅ 完成', color: '#22c55e' };
  if (diff < 0) return { label: `⚠ 少${Math.abs(diff).toFixed(1)}吨`, color: '#f59e0b' };
  return { label: `🔴 超${diff.toFixed(1)}吨`, color: '#ef4444' };
}

export default function TrackingTab({ user, formulas, premixes }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showAddArrival, setShowAddArrival] = useState(null);

  const canEdit = ['admin', 'procurement'].includes(user.role);
  const canArrival = ['admin', 'procurement', 'warehouse', 'factory'].includes(user.role);

  const load = async () => {
    try {
      const res = await api.get('/orders');
      setOrders(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={s.loading}>加载中...</div>;

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.title}>生产追踪</h2>
        {canEdit && <button style={s.btnPrimary} onClick={() => setShowAddOrder(true)}>+ 新建计划单</button>}
      </div>

      {/* Mobile cards */}
      <div style={s.mobileList}>
        {orders.map(o => {
          const latestF = getActiveFormula(formulas, o.product, new Date().toISOString().slice(0, 10));
          const status = calcStatus(o.planItems, o.arrivals);
          return (
            <div key={o.id} style={s.mobileCard}>
              <div style={s.mobileCardTop}>
                <strong style={s.mobileProduct}>{o.product}</strong>
                <span style={{ ...s.statusBadge, background: status.color + '22', color: status.color }}>{status.label}</span>
              </div>
              <div style={s.mobileRow}><span style={s.mobileLabel}>价格</span><span style={s.price}>¥{latestF?.price?.toLocaleString() ?? '—'}/吨</span></div>
              <div style={s.mobileRow}><span style={s.mobileLabel}>配方</span><span style={s.badge}>{latestF?.version ?? '—'}</span></div>
              <div style={s.mobileRow}><span style={s.mobileLabel}>月份</span><span>{o.month}</span></div>
              <div style={s.mobileRow}><span style={s.mobileLabel}>计划</span><span style={{ fontSize: 12, color: '#93c5fd' }}>{planSummary(o.planItems)}</span></div>
              <div style={s.mobileRow}>
                <span style={s.mobileLabel}>已到</span>
                <span>{(o.arrivals || []).reduce((s, a) => s + parseFloat(a.tons), 0).toFixed(1)}吨 / {(o.planItems || []).reduce((s, p) => s + p.planned_trucks * p.tons_per_truck, 0).toFixed(1)}吨</span>
              </div>
              <div style={s.mobileBtns}>
                <button style={s.btnSmall} onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                  {expanded === o.id ? '收起' : '详情'}
                </button>
                {canArrival && <button style={{ ...s.btnSmall, ...s.btnGreen }} onClick={() => setShowAddArrival(o.id)}>+ 到货</button>}
              </div>
              {expanded === o.id && <OrderDetail order={o} formulas={formulas} premixes={premixes} />}
            </div>
          );
        })}
        {orders.length === 0 && <div style={s.empty}>暂无计划单</div>}
      </div>

      {/* Desktop table */}
      <div style={s.desktopTable}>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>{['产品', '价格(元/吨)', '配方版本', '月份', '计划', '实际进度', '操作'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const latestF = getActiveFormula(formulas, o.product, new Date().toISOString().slice(0, 10));
                const status = calcStatus(o.planItems, o.arrivals);
                return (
                  <>
                    <tr key={o.id} style={s.tr}>
                      <td style={s.td}><strong>{o.product}</strong></td>
                      <td style={s.td}><span style={s.price}>¥{latestF?.price?.toLocaleString() ?? '—'}</span></td>
                      <td style={s.td}>
                        <span style={s.badge}>{latestF?.version ?? '—'}</span>
                        <div style={s.sub}>{latestF?.effective_date}</div>
                      </td>
                      <td style={s.td}>{o.month}</td>
                      <td style={s.td}>
                        <div style={{ color: '#93c5fd', fontSize: 12 }}>{planSummary(o.planItems)}</div>
                        <div style={s.sub}>共{(o.planItems||[]).reduce((s,p)=>s+p.planned_trucks,0)}车 / {(o.planItems||[]).reduce((s,p)=>s+p.planned_trucks*p.tons_per_truck,0).toFixed(1)}吨</div>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.statusBadge, background: status.color + '22', color: status.color }}>{status.label}</span>
                        <div style={s.sub}>已到{(o.arrivals||[]).reduce((s,a)=>s+parseFloat(a.tons),0).toFixed(1)}吨</div>
                      </td>
                      <td style={s.td}>
                        <button style={s.btnSmall} onClick={() => setExpanded(expanded === o.id ? null : o.id)}>{expanded === o.id ? '收起' : '详情'}</button>
                        {canArrival && <button style={{ ...s.btnSmall, ...s.btnGreen }} onClick={() => setShowAddArrival(o.id)}>+ 到货</button>}
                      </td>
                    </tr>
                    {expanded === o.id && (
                      <tr key={o.id + '-d'}>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <OrderDetail order={o} formulas={formulas} premixes={premixes} />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {orders.length === 0 && <tr><td colSpan={7} style={s.empty}>暂无计划单</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showAddOrder && (
        <AddOrderModal formulas={formulas} onClose={() => setShowAddOrder(false)}
          onSave={async data => { await api.post('/orders', data); setShowAddOrder(false); load(); }} />
      )}
      {showAddArrival && (
        <AddArrivalModal
          order={orders.find(o => o.id === showAddArrival)}
          formulas={formulas} premixes={premixes}
          onClose={() => setShowAddArrival(null)}
          onSave={async data => { await api.post(`/orders/${showAddArrival}/arrivals`, data); setShowAddArrival(null); load(); }} />
      )}
    </div>
  );
}

function OrderDetail({ order, formulas, premixes }) {
  return (
    <div style={s.detail}>
      <div style={s.detailGrid}>
        <div>
          <div style={s.detailLabel}>📋 计划明细</div>
          <table style={{ ...s.table, marginTop: 8 }}>
            <thead><tr>{['牛场', '计划车数', '吨/车', '计划吨'].map(h => <th key={h} style={s.thSm}>{h}</th>)}</tr></thead>
            <tbody>
              {(order.planItems || []).map((p, i) => (
                <tr key={i} style={s.tr}>
                  <td style={s.tdSm}>{p.farm}</td>
                  <td style={s.tdSm}>{p.planned_trucks}车</td>
                  <td style={s.tdSm}>{p.tons_per_truck}吨</td>
                  <td style={s.tdSm}>{(p.planned_trucks * p.tons_per_truck).toFixed(1)}吨</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <div style={s.detailLabel}>🚛 实际到货记录</div>
          {!(order.arrivals?.length) ? <div style={{ color: '#334155', fontSize: 12, padding: '12px 0' }}>暂无到货</div> : (
            <table style={{ ...s.table, marginTop: 8 }}>
              <thead><tr>{['牛场', '日期', '车', '吨', '配方', '预混料用量', '价格核对'].map(h => <th key={h} style={s.thSm}>{h}</th>)}</tr></thead>
              <tbody>
                {order.arrivals.map(a => {
                  const f = formulas.find(f => f.id === a.formula_id);
                  const expectedF = getActiveFormula(formulas, order.product, a.arrival_date);
                  const priceOk = f?.price === expectedF?.price;
                  return (
                    <tr key={a.id} style={s.tr}>
                      <td style={s.tdSm}>{a.farm}</td>
                      <td style={s.tdSm}>{a.arrival_date?.slice(0,10)}</td>
                      <td style={s.tdSm}>{a.trucks}</td>
                      <td style={s.tdSm}>{a.tons}</td>
                      <td style={s.tdSm}><span style={s.badge}>{f?.version}</span></td>
                      <td style={s.tdSm}>
                        {f?.premixItems?.map(item => {
                          const pm = premixes.find(p => p.id === item.premixId);
                          return <div key={item.premixId} style={{ fontSize: 11 }}>{pm?.name}：{item.kgPerTon}kg/吨 × {a.tons}吨 = <strong>{(a.tons * item.kgPerTon).toFixed(0)}kg</strong></div>;
                        })}
                      </td>
                      <td style={s.tdSm}>
                        {priceOk
                          ? <span style={{ color: '#22c55e' }}>✅ ¥{f?.price}</span>
                          : <span style={{ color: '#ef4444' }}>❌ 应¥{expectedF?.price}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function AddOrderModal({ formulas, onClose, onSave }) {
  const products = [...new Set(formulas.map(f => f.product))];
  const [product, setProduct] = useState(products[0] || '');
  const [newProduct, setNewProduct] = useState('');
  const [isNew, setIsNew] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [planItems, setPlanItems] = useState([{ farm: '1场', plannedTrucks: 1, tonsPerTruck: 33.6 }]);
  const [saving, setSaving] = useState(false);

  const update = (i, field, val) => setPlanItems(p => p.map((item, idx) => idx === i ? { ...item, [field]: field === 'farm' ? val : Number(val) } : item));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ product: isNew ? newProduct : product, month, planItems });
    } finally { setSaving(false); }
  };

  return (
    <Modal title="新建计划单" onClose={onClose}>
      <div style={s.formGrid}>
        <label style={s.label}>产品</label>
        <div>
          <select style={s.input} value={isNew ? '__new__' : product}
            onChange={e => { if (e.target.value === '__new__') setIsNew(true); else { setIsNew(false); setProduct(e.target.value); } }}>
            {products.map(p => <option key={p}>{p}</option>)}
            <option value="__new__">+ 新产品</option>
          </select>
          {isNew && <input style={{ ...s.input, marginTop: 8 }} placeholder="输入新产品名称" value={newProduct} onChange={e => setNewProduct(e.target.value)} />}
        </div>
        <label style={s.label}>月份</label>
        <input style={s.input} type="month" value={month} onChange={e => setMonth(e.target.value)} />
      </div>
      <div style={{ padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={s.label}>计划明细</span>
          <button style={s.btnSmall} onClick={() => setPlanItems(p => [...p, { farm: '1场', plannedTrucks: 1, tonsPerTruck: 33.6 }])}>+ 添加牛场</button>
        </div>
        {planItems.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <select style={{ ...s.inputSm, flex: 1 }} value={item.farm} onChange={e => update(i, 'farm', e.target.value)}>
              {FARMS.map(f => <option key={f}>{f}</option>)}
            </select>
            <input style={{ ...s.inputSm, flex: 1 }} type="number" placeholder="车数" value={item.plannedTrucks} onChange={e => update(i, 'plannedTrucks', e.target.value)} />
            <input style={{ ...s.inputSm, flex: 1 }} type="number" placeholder="吨/车" value={item.tonsPerTruck} onChange={e => update(i, 'tonsPerTruck', e.target.value)} />
            <button style={{ ...s.btnSmall, color: '#ef4444' }} onClick={() => setPlanItems(p => p.filter((_, idx) => idx !== i))}>✕</button>
          </div>
        ))}
      </div>
      <div style={s.modalFooter}>
        <button style={s.btnSec} onClick={onClose}>取消</button>
        <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? '保存中...' : '保存'}</button>
      </div>
    </Modal>
  );
}

function AddArrivalModal({ order, formulas, premixes, onClose, onSave }) {
  const available = formulas.filter(f => f.product === order.product);
  const today = new Date().toISOString().slice(0, 10);
  const defaultF = getActiveFormula(formulas, order.product, today);
  const [form, setForm] = useState({
    farm: order.planItems?.[0]?.farm || '1场',
    arrival_date: today, trucks: 1,
    tons: order.planItems?.[0]?.tons_per_truck || 33.6,
    formula_id: defaultF?.id || available[0]?.id,
  });
  const [saving, setSaving] = useState(false);
  const selF = formulas.find(f => f.id === Number(form.formula_id) || f.id === form.formula_id);
  const expF = getActiveFormula(formulas, order.product, form.arrival_date);
  const priceOk = selF?.id === expF?.id;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <Modal title={`录入到货 — ${order.product} ${order.month}`} onClose={onClose}>
      <div style={s.formGrid}>
        <label style={s.label}>牛场</label>
        <select style={s.input} value={form.farm} onChange={e => setForm(p => ({ ...p, farm: e.target.value }))}>
          {FARMS.map(f => <option key={f}>{f}</option>)}
        </select>
        <label style={s.label}>到货日期</label>
        <input style={s.input} type="date" value={form.arrival_date} onChange={e => setForm(p => ({ ...p, arrival_date: e.target.value }))} />
        <label style={s.label}>车数</label>
        <input style={s.input} type="number" value={form.trucks} onChange={e => setForm(p => ({ ...p, trucks: Number(e.target.value) }))} />
        <label style={s.label}>吨数</label>
        <input style={s.input} type="number" value={form.tons} onChange={e => setForm(p => ({ ...p, tons: Number(e.target.value) }))} />
        <label style={s.label}>使用配方</label>
        <select style={s.input} value={form.formula_id} onChange={e => setForm(p => ({ ...p, formula_id: e.target.value }))}>
          {available.map(f => <option key={f.id} value={f.id}>{f.version} — ¥{f.price}/吨 (生效:{f.effective_date})</option>)}
        </select>
      </div>
      {!priceOk && (
        <div style={{ margin: '0 24px 12px', background: '#7c2d1222', border: '1px solid #92400e', borderRadius: 8, padding: '10px 14px', color: '#fbbf24', fontSize: 13 }}>
          ⚠ 所选配方与该日期应使用配方不符！应使用 {expF?.version}（¥{expF?.price}/吨）
        </div>
      )}
      {selF && (
        <div style={{ margin: '0 24px 12px', background: '#0f2942', border: '1px solid #1e3a5f', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#93c5fd' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>预混料用量预览</div>
          {selF.premixItems?.map(item => {
            const pm = premixes.find(p => p.id === item.premixId);
            return <div key={item.premixId}>{pm?.name}：{item.kgPerTon} kg/吨 × {form.tons}吨 = <strong>{(form.tons * item.kgPerTon).toFixed(0)} kg</strong></div>;
          })}
        </div>
      )}
      <div style={s.modalFooter}>
        <button style={s.btnSec} onClick={onClose}>取消</button>
        <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? '保存中...' : '确认到货'}</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={s.modalHeader}>
          <span style={s.modalTitle}>{title}</span>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const s = {
  loading: { color: '#475569', padding: 40, textAlign: 'center' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  title: { fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  mobileList: { display: 'none' },
  desktopTable: { display: 'block' },
  '@media(max-width:768px)': { mobileList: { display: 'block' }, desktopTable: { display: 'none' } },
  mobileCard: { background: '#0d1529', border: '1px solid #1e2d4a', borderRadius: 12, padding: 16, marginBottom: 12 },
  mobileCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  mobileProduct: { fontSize: 15, color: '#f1f5f9' },
  mobileRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #1a2540', fontSize: 13, color: '#cbd5e1' },
  mobileLabel: { color: '#475569', fontSize: 12 },
  mobileBtns: { display: 'flex', gap: 8, marginTop: 10 },
  tableWrap: { overflowX: 'auto', borderRadius: 10, border: '1px solid #1e2d4a' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#0d1529', color: '#64748b', padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 12, letterSpacing: '0.06em', borderBottom: '1px solid #1e2d4a', whiteSpace: 'nowrap' },
  thSm: { background: '#0a1628', color: '#475569', padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, borderBottom: '1px solid #1e2d4a' },
  tr: { borderBottom: '1px solid #1a2540' },
  td: { padding: '12px 14px', verticalAlign: 'top', color: '#cbd5e1' },
  tdSm: { padding: '8px 12px', verticalAlign: 'top', color: '#94a3b8', fontSize: 12 },
  badge: { display: 'inline-block', background: '#1e3a5f', color: '#38bdf8', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  price: { color: '#34d399', fontWeight: 700 },
  sub: { color: '#475569', fontSize: 11, marginTop: 3 },
  statusBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  btnPrimary: { background: '#1d4ed8', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  btnSec: { background: '#1e2d4a', color: '#94a3b8', border: '1px solid #2d3f5f', padding: '8px 18px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  btnSmall: { background: '#1a2540', color: '#94a3b8', border: '1px solid #2d3f5f', padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', marginRight: 4 },
  btnGreen: { background: '#14532d', color: '#4ade80', borderColor: '#166534' },
  detail: { background: '#070d1a', padding: 20, borderTop: '1px solid #1e2d4a' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 },
  detailLabel: { fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em' },
  empty: { color: '#334155', fontSize: 13, padding: 24, textAlign: 'center' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#0d1529', border: '1px solid #1e2d4a', borderRadius: 14, width: 'min(600px,95vw)', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #1e2d4a' },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#f1f5f9' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #1e2d4a', marginTop: 8 },
  formGrid: { display: 'grid', gridTemplateColumns: '100px 1fr', gap: '10px 16px', padding: '20px 24px', alignItems: 'center' },
  label: { fontSize: 12, color: '#64748b', fontWeight: 600, textAlign: 'right' },
  input: { background: '#0a1628', border: '1px solid #2d3f5f', color: '#e2e8f0', padding: '8px 12px', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  inputSm: { background: '#0a1628', border: '1px solid #2d3f5f', color: '#e2e8f0', padding: '6px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' },
};
